
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useModeration } from '../hooks/useModeration';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertTriangle, CheckCircle, XCircle, Eye, Trash2, AlertOctagon, Archive } from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { format } from 'date-fns';
import BottomTabNavigation from '../components/navigation/BottomTabNavigation';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { deleteReport, archiveReport } from '../services/moderationService';
import { DeleteConfirmDialog } from '../components/DeleteConfirmDialog';

const ModerationPanel = () => {
  const { t } = useTranslation();
  const { reports, isLoading, isModerator, updateReport, isUpdating } = useModeration();
  const [selectedReport, setSelectedReport] = useState<any>(null);
  const [moderatorNotes, setModeratorNotes] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const deletePostMutation = useMutation({
    mutationFn: async (postId: string) => {
      const { error } = await supabase
        .from('memory_posts')
        .delete()
        .eq('id', postId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      toast.success(t('adminPanel.moderation.postDeletedSuccess'));
    },
    onError: (error: any) => {
      toast.error(t('adminPanel.moderation.postDeleteError') + ': ' + error.message);
    },
  });

  const deleteReportMutation = useMutation({
    mutationFn: deleteReport,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      toast.success(t('adminPanel.moderation.reportDeleted'));
      setDeleteTarget(null);
    },
    onError: (error: any) => {
      toast.error(t('adminPanel.moderation.reportDeleteError') + ': ' + error.message);
    },
  });

  const archiveReportMutation = useMutation({
    mutationFn: archiveReport,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      toast.success(t('adminPanel.moderation.reportArchived'));
    },
  });

  const sendWarningMutation = useMutation({
    mutationFn: async ({ userId, postId, reason }: { userId: string; postId: string; reason: string }) => {
      const { error } = await supabase
        .from('notifications')
        .insert({
          recipient_user_id: userId,
          type: 'content_warning',
          content_preview: t('adminPanel.moderation.warningMessage', { reason }),
          related_entity_type: 'post',
          related_entity_id: postId,
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t('adminPanel.moderation.warningSentSuccess'));
    },
    onError: (error: any) => {
      toast.error(t('adminPanel.moderation.warningSentError') + ': ' + error.message);
    },
  });

  if (!isModerator) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex-1 container mx-auto max-w-4xl p-4">
          <Card>
            <CardContent className="flex items-center justify-center py-12">
              <div className="text-center">
                <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">{t('adminPanel.moderation.accessDenied')}</h3>
                <p className="text-muted-foreground">
                  {t('adminPanel.moderation.noModeratorPrivileges')}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
        <BottomTabNavigation />
      </div>
    );
  }

  const handleUpdateStatus = (reportId: string, status: string) => {
    updateReport({ 
      reportId, 
      status, 
      notes: moderatorNotes || undefined 
    });
    toast.success(t('adminPanel.moderation.reportStatusUpdated', { status: t(`adminPanel.moderation.status.${status}`) }));
    setModeratorNotes('');
    setSelectedReport(null);
    setIsDialogOpen(false);
  };

  const handleApproveAndDelete = async (report: any) => {
    try {
      updateReport({ 
        reportId: report.id, 
        status: 'approved', 
        notes: moderatorNotes || undefined 
      });

      await deletePostMutation.mutateAsync(report.post_id);

      const { data: postData } = await supabase
        .from('memory_posts')
        .select('user_id')
        .eq('id', report.post_id)
        .single();

      if (postData) {
        await sendWarningMutation.mutateAsync({
          userId: postData.user_id,
          postId: report.post_id,
          reason: report.reason
        });
      }

      setModeratorNotes('');
      setSelectedReport(null);
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Error in approve and delete flow:', error);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary">{t('adminPanel.moderation.status.pending')}</Badge>;
      case 'approved':
        return <Badge variant="destructive">{t('adminPanel.moderation.status.approved')}</Badge>;
      case 'rejected':
        return <Badge variant="default">{t('adminPanel.moderation.status.rejected')}</Badge>;
      case 'archived':
        return <Badge variant="outline" className="text-muted-foreground">{t('adminPanel.moderation.archiveReport')}</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex-1 container mx-auto max-w-4xl p-4 space-y-4">
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <CardContent className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-8 w-24" />
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
        <BottomTabNavigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 container mx-auto max-w-4xl p-4 pb-24">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              {t('adminPanel.moderation.title')}
              <Badge variant="outline">{t('adminPanel.moderation.reportsCount', { count: reports.length })}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {reports.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">{t('adminPanel.moderation.noReports')}</h3>
                <p className="text-muted-foreground">
                  {t('adminPanel.moderation.allPostsGood')}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {reports.map((report) => (
                  <Card key={report.id} className="border-l-4 border-l-orange-500">
                    <CardContent className="pt-4">
                      {/* Mobile-optimized layout: flex-col on mobile, row on larger screens */}
                      <div className="flex flex-col gap-3 mb-3">
                        {/* Top row: Info + action icons */}
                        <div className="flex justify-between items-start gap-2">
                          <div className="space-y-1 flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-semibold text-sm sm:text-base">{t('adminPanel.moderation.reportNumber', { id: report.id.slice(0, 8) })}</h4>
                              {getStatusBadge(report.status)}
                            </div>
                            <p className="text-sm text-muted-foreground truncate">
                              {t('adminPanel.moderation.reportedBy')}: {report.reporter_name || t('adminPanel.moderation.anonymous')}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {format(new Date(report.created_at), 'dd MMM yyyy HH:mm')}
                            </p>
                          </div>
                          
                          {/* Action icons - always visible top right */}
                          <div className="flex items-center gap-1 shrink-0">
                            {/* Archive button */}
                            {report.status !== 'archived' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => archiveReportMutation.mutate(report.id)}
                                disabled={archiveReportMutation.isPending}
                                title={t('adminPanel.moderation.archiveReport')}
                              >
                                <Archive className="h-4 w-4" />
                              </Button>
                            )}
                            
                            {/* Delete button */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteTarget(report.id)}
                              disabled={deleteReportMutation.isPending}
                              className="text-destructive hover:text-destructive"
                              title={t('adminPanel.moderation.deleteReport')}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Review button - full width on mobile, auto on larger screens */}
                        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                          <DialogTrigger asChild>
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="w-full sm:w-auto"
                              onClick={() => setSelectedReport(report)}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              {t('adminPanel.moderation.review')}
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-2xl">
                            <DialogHeader>
                              <DialogTitle>{t('adminPanel.moderation.reportDetails')}</DialogTitle>
                            </DialogHeader>
                            {selectedReport && (
                              <div className="space-y-4">
                                <div>
                                  <h4 className="font-semibold mb-2">{t('adminPanel.moderation.postInformation')}</h4>
                                  <p><strong>{t('adminPanel.moderation.pet')}:</strong> {selectedReport.pet_name || t('adminPanel.moderation.unknown')}</p>
                                  <p><strong>{t('adminPanel.moderation.caption')}:</strong> {selectedReport.post_caption || t('adminPanel.moderation.noCaption')}</p>
                                  <p><strong>{t('adminPanel.moderation.postId')}:</strong> {selectedReport.post_id}</p>
                                </div>
                                
                                <div>
                                  <h4 className="font-semibold mb-2">{t('adminPanel.moderation.reportDetailsSection')}</h4>
                                  <p><strong>{t('adminPanel.moderation.reason')}:</strong> {selectedReport.reason}</p>
                                  {selectedReport.description && (
                                    <p><strong>{t('adminPanel.moderation.description')}:</strong> {selectedReport.description}</p>
                                  )}
                                  <p><strong>{t('adminPanel.moderation.reportedBy')}:</strong> {selectedReport.reporter_name || t('adminPanel.moderation.anonymous')}</p>
                                </div>

                                {selectedReport.status === 'pending' && (
                                  <div className="space-y-3">
                                    <div>
                                      <label className="text-sm font-medium">{t('adminPanel.moderation.moderatorNotesOptional')}</label>
                                      <Textarea
                                        value={moderatorNotes}
                                        onChange={(e) => setModeratorNotes(e.target.value)}
                                        placeholder={t('adminPanel.moderation.addNotesPlaceholder')}
                                        className="mt-1"
                                      />
                                    </div>
                                    
                                    <div className="flex gap-2 flex-wrap">
                                      <Button
                                        onClick={() => handleApproveAndDelete(selectedReport)}
                                        disabled={isUpdating || deletePostMutation.isPending}
                                        variant="destructive"
                                        size="sm"
                                        className="flex items-center gap-2"
                                      >
                                        <AlertOctagon className="h-4 w-4" />
                                        {t('adminPanel.moderation.approveAndDelete')}
                                      </Button>
                                      <Button
                                        onClick={() => handleUpdateStatus(selectedReport.id, 'approved')}
                                        disabled={isUpdating}
                                        variant="outline"
                                        size="sm"
                                      >
                                        <CheckCircle className="h-4 w-4 mr-2" />
                                        {t('adminPanel.moderation.approveOnly')}
                                      </Button>
                                      <Button
                                        onClick={() => handleUpdateStatus(selectedReport.id, 'rejected')}
                                        disabled={isUpdating}
                                        variant="outline"
                                        size="sm"
                                      >
                                        <XCircle className="h-4 w-4 mr-2" />
                                        {t('adminPanel.moderation.rejectReport')}
                                      </Button>
                                    </div>
                                  </div>
                                )}

                                {selectedReport.moderator_notes && (
                                  <div>
                                    <h4 className="font-semibold mb-2">{t('adminPanel.moderation.moderatorNotes')}</h4>
                                    <p className="text-sm bg-muted p-3 rounded">
                                      {selectedReport.moderator_notes}
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </DialogContent>
                        </Dialog>
                      </div>
                      
                      <div className="space-y-2">
                        <p><strong>{t('adminPanel.moderation.reason')}:</strong> {report.reason}</p>
                        <p><strong>{t('adminPanel.moderation.pet')}:</strong> {report.pet_name || t('adminPanel.moderation.unknown')}</p>
                        {report.description && (
                          <p><strong>{t('adminPanel.moderation.details')}:</strong> {report.description}</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      <DeleteConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteReportMutation.mutate(deleteTarget)}
        title={t('adminPanel.moderation.confirmDeleteReportTitle')}
        description={t('adminPanel.moderation.confirmDeleteReport')}
        itemName={deleteTarget?.slice(0, 8) || ''}
        isLoading={deleteReportMutation.isPending}
      />
      
      <BottomTabNavigation />
    </div>
  );
};

export default ModerationPanel;
